import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Stack,
    useMediaQuery,
    useTheme,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import { queryItemCategories, queryEvents } from '../schema';
import { LocationData } from '../utils/inventory';

export type SortOption = 'name-asc' | 'name-desc' | 'category' | 'quantity-high' | 'quantity-low';

interface Props {
    searchText: string;
    categoryId: string | null;
    locationId: string | null;
    consumableOnly: boolean;
    sortBy: SortOption;
    onSearchChange: (text: string) => void;
    onCategoryChange: (categoryId: string | null) => void;
    onLocationChange: (locationId: string | null) => void;
    onConsumableOnlyChange: (consumableOnly: boolean) => void;
    onSortChange: (sort: SortOption) => void;
    locationDataMap: Map<string, LocationData>;
}

export default function InventorySearchBar({
    searchText,
    categoryId,
    locationId,
    consumableOnly,
    sortBy,
    onSearchChange,
    onCategoryChange,
    onLocationChange,
    onConsumableOnlyChange,
    onSortChange,
    locationDataMap,
}: Props) {
    const [debouncedSearch, setDebouncedSearch] = useState(searchText);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { data: categoriesData } = queryItemCategories();
    const { data: eventsData } = queryEvents();

    const categories = useMemo(() => categoriesData?.itemCategories || [], [categoriesData]);
    const events = useMemo(() => eventsData?.events || [], [eventsData]);

    // Get storage locations that have inventory (from locationDataMap)
    const storageLocationsWithInventory = useMemo(() => {
        const seen = new Set<string>();
        const out: { id: string; name: string }[] = [];
        for (const locData of Array.from(locationDataMap.values())) {
            for (const s of locData.storageLocations) {
                if (!seen.has(s.id)) {
                    seen.add(s.id);
                    out.push({ id: s.id, name: s.name });
                }
            }
        }
        return out;
    }, [locationDataMap]);

    // Get events that have inventory
    const eventsWithInventory = useMemo(() => {
        return events.filter(event => {
            // Check if any item has inventory at this event
            for (const [, locationData] of Array.from(locationDataMap.entries())) {
                if (locationData.events.some((e: { eventId: string; eventName: string; quantity: number }) => e.eventId === event.id)) {
                    return true;
                }
            }
            return false;
        });
    }, [events, locationDataMap]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            onSearchChange(debouncedSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [debouncedSearch, onSearchChange]);

    const hasActiveFilters = searchText || categoryId || locationId || consumableOnly || sortBy !== 'name-asc';

    const handleClearFilters = () => {
        setDebouncedSearch('');
        onSearchChange('');
        onCategoryChange(null);
        onLocationChange(null);
        onConsumableOnlyChange(false);
        onSortChange('name-asc');
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Stack spacing={2} direction={isMobile ? 'column' : 'row'} sx={{ alignItems: isMobile ? 'stretch' : 'flex-end' }}>
                {/* Search */}
                <TextField
                    fullWidth={isMobile}
                    size="small"
                    placeholder="Search items..."
                    value={debouncedSearch}
                    onChange={e => setDebouncedSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    sx={{ flex: isMobile ? 'none' : 1 }}
                />

                {/* Category Filter */}
                <FormControl size="small" sx={{ minWidth: 150, flex: isMobile ? 'none' : '0 0 auto' }}>
                    <InputLabel>Category</InputLabel>
                    <Select value={categoryId || ''} label="Category" onChange={e => onCategoryChange(e.target.value || null)}>
                        <MenuItem value="">All Categories</MenuItem>
                        {categories.map(category => (
                            <MenuItem key={category.id} value={category.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {category.color && (
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                bgcolor: category.color,
                                                border: '1px solid rgba(0,0,0,0.1)',
                                            }}
                                        />
                                    )}
                                    {category.name}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Location Filter */}
                <FormControl size="small" sx={{ minWidth: 150, flex: isMobile ? 'none' : '0 0 auto' }}>
                    <InputLabel>Location</InputLabel>
                    <Select value={locationId || ''} label="Location" onChange={e => onLocationChange(e.target.value || null)}>
                        <MenuItem value="">All Locations</MenuItem>
                        {storageLocationsWithInventory.map((s: { id: string; name: string }) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.name}
                            </MenuItem>
                        ))}
                        {eventsWithInventory.map(event => (
                            <MenuItem key={event.id} value={event.id}>
                                {event.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Consumables Only Filter */}
                <FormControlLabel
                    control={<Checkbox checked={consumableOnly} onChange={e => onConsumableOnlyChange(e.target.checked)} size="small" />}
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocalGasStationIcon sx={{ fontSize: 18 }} />
                            <span>Consumables Only</span>
                        </Box>
                    }
                    sx={{ flex: isMobile ? 'none' : '0 0 auto' }}
                />

                {/* Sort */}
                <FormControl size="small" sx={{ minWidth: 150, flex: isMobile ? 'none' : '0 0 auto' }}>
                    <InputLabel>Sort</InputLabel>
                    <Select value={sortBy} label="Sort" onChange={e => onSortChange(e.target.value as SortOption)}>
                        <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                        <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                        <MenuItem value="category">Category</MenuItem>
                        <MenuItem value="quantity-high">Total Quantity (High to Low)</MenuItem>
                        <MenuItem value="quantity-low">Total Quantity (Low to High)</MenuItem>
                    </Select>
                </FormControl>

                {/* Clear Filters */}
                {hasActiveFilters && (
                    <Button
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={handleClearFilters}
                        sx={{ flex: isMobile ? 'none' : '0 0 auto' }}
                    >
                        Clear
                    </Button>
                )}
            </Stack>
        </Box>
    );
}
